import React, { useCallback, useEffect, useState } from 'react';
import { BlockedDaysPanel } from '../components/BlockedDaysPanel';
import { TodayPanel } from '../components/TodayPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'fenicis_admin_token';

const formatError = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(' ');
  return 'Une erreur est survenue.';
};

const fieldStyle = { padding: '12px 14px', border: '1px solid var(--border-light)', background: 'var(--bg-primary)', fontSize: '15px' };
const cell = { padding: '12px 8px', fontSize: '14px', textAlign: 'left', verticalAlign: 'top' };
const frDate = (iso) => new Date(iso).toLocaleDateString('fr-FR');
const frDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const FORMATS = { call: 'Appel', video: 'Visio', meeting: 'Rencontre à Paris' };

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const res = await fetch(`${API}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) return setError(formatError(data.detail));
    onLogin(data.token);
  };

  return (
    <form onSubmit={submit} data-testid="admin-login-form" style={{ maxWidth: '380px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <h1 className="heading-2" style={{ marginBottom: '8px' }}>Espace privé</h1>
      <input data-testid="admin-login-email-input" type="email" required placeholder="Adresse e-mail" value={email} onChange={(e) => setEmail(e.target.value)} style={fieldStyle} />
      <input data-testid="admin-login-password-input" type="password" required placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} style={fieldStyle} />
      {error && <p data-testid="admin-login-error" className="body-small" style={{ color: '#9b2c2c' }}>{error}</p>}
      <button data-testid="admin-login-submit-button" type="submit" className="btn-primary">Se connecter</button>
    </form>
  );
};

const downloadCsv = (rows, columns, name) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map((r) => columns.map((c) => esc(c.csv ? c.csv(r) : r[c.key])).join(';'));
  const csv = '\ufeff' + [columns.map((c) => c.label).join(';')].concat(lines).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  a.download = `fenicis-${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const TABS = {
  subscribers: {
    label: 'Inscrits', path: '/admin/subscribers', csv: 'inscrits', removable: true, empty: 'Aucun inscrit pour le moment.',
    confirm: (r) => `Retirer ${r.email} de la liste ?`,
    columns: [
      { key: 'email', label: 'E-mail' },
      { key: 'language', label: 'Langue', render: (r) => r.language.toUpperCase() },
      { key: 'source', label: 'Origine', render: (r) => (r.source === 'contact' ? 'Page Contact' : 'Pied de page') },
      { key: 'created_at', label: 'Date', render: (r) => frDate(r.created_at), csv: (r) => frDate(r.created_at) }
    ]
  },
  inquiries: {
    label: 'Demandes', path: '/inquiries', csv: 'demandes', removable: false, empty: 'Aucune demande pour le moment.',
    columns: [
      { key: 'created_at', label: 'Date', render: (r) => frDate(r.created_at), csv: (r) => frDate(r.created_at) },
      { key: 'full_name', label: 'Nom' },
      { key: 'email', label: 'E-mail', render: (r) => <a href={`mailto:${r.email}`}>{r.email}</a> },
      { key: 'company', label: 'Entreprise' },
      { key: 'phone', label: 'Téléphone' },
      { key: 'message', label: 'Message', render: (r) => <span style={{ whiteSpace: 'pre-wrap' }}>{r.message}</span> }
    ]
  },
  bookings: {
    label: 'Rendez-vous', path: '/admin/bookings', csv: 'rendez-vous', removable: true, empty: 'Aucun rendez-vous pour le moment.',
    confirm: (r) => `Annuler le rendez-vous de ${r.full_name} (${frDay(r.date)} à ${r.time}) ? Le créneau redeviendra disponible.`,
    columns: [
      { key: 'date', label: 'Date', render: (r) => (<>{frDay(r.date)}{r.blockedDay && <span data-testid={`admin-booking-blocked-${r.id}`} className="body-small" style={{ display: 'block', color: '#9b2c2c' }}>Jour bloqué</span>}</>), csv: (r) => frDay(r.date) },
      { key: 'time', label: 'Heure' },
      { key: 'format', label: 'Format', render: (r) => FORMATS[r.format] || '—', csv: (r) => FORMATS[r.format] || '' },
      { key: 'full_name', label: 'Nom' },
      { key: 'email', label: 'E-mail', render: (r) => <a href={`mailto:${r.email}`}>{r.email}</a> },
      { key: 'phone', label: 'Téléphone' },
      { key: 'location', label: 'Lieu / lien', render: (r) => (r.format === 'meeting' ? r.location : r.format === 'video' && r.video_link ? 'Lien envoyé' : '') || '—', csv: (r) => r.location || r.video_link || '' },
      { key: 'message', label: 'Projet', render: (r) => <span style={{ whiteSpace: 'pre-wrap' }}>{r.message || '—'}</span> }
    ]
  },
  blocked: { label: 'Absences', path: '/admin/blocked-days' }
};

const DataTable = ({ tab, rows, onDelete, extra }) => (
  <div style={{ overflowX: 'auto' }}>
    <table data-testid="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr className="meta-text" style={{ color: 'var(--text-light)' }}>
          {tab.columns.map((c) => <th key={c.key} style={cell}>{c.label}</th>)}
          {tab.removable && <th style={cell} />}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} data-testid={`admin-row-${r.id}`} style={{ borderBottom: '1px solid var(--border-light)' }}>
            {tab.columns.map((c) => <td key={c.key} style={cell}>{c.render ? c.render(r) : r[c.key] || '—'}</td>)}
            {tab.removable && (
              <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {extra && extra(r)}
                <button data-testid={`admin-delete-${r.id}`} onClick={() => onDelete(r)} className="body-small" style={{ background: 'none', border: 'none', color: '#9b2c2c', cursor: 'pointer' }}>
                  {tab === TABS.bookings ? 'Annuler' : 'Retirer'}
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AdminSubscribers() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [active, setActive] = useState('subscribers');
  const [data, setData] = useState({});

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setData({});
  }, []);

  const authFetch = useCallback(async (path, opts = {}) => {
    const headers = { Authorization: `Bearer ${token}`, ...(opts.json ? { 'Content-Type': 'application/json' } : {}) };
    const res = await fetch(`${API}${path}`, { method: opts.method, body: opts.body, headers });
    if (res.status === 401) logout();
    return res;
  }, [token, logout]);

  useEffect(() => {
    if (!token) return;
    Object.entries(TABS).forEach(([key, tab]) => {
      authFetch(tab.path).then((r) => (r.ok ? r.json() : null)).then((d) => d && setData((prev) => ({ ...prev, [key]: d })));
    });
  }, [token, authFetch]);

  const login = (tok) => {
    localStorage.setItem(TOKEN_KEY, tok);
    setToken(tok);
  };

  const tab = TABS[active];
  const [videoLink, setVideoLink] = useState('');

  useEffect(() => {
    if (!token) return;
    authFetch('/admin/settings').then((r) => (r.ok ? r.json() : null)).then((d) => d && setVideoLink(d.video_link || ''));
  }, [token, authFetch]);

  const saveVideoLink = async () => {
    const res = await authFetch('/admin/settings', { method: 'PUT', body: JSON.stringify({ video_link: videoLink }), json: true });
    const out = await res.json();
    window.alert(res.ok ? 'Lien visio habituel enregistré.' : formatError(out.detail));
  };

  const updateBooking = (b) => setData((prev) => ({ ...prev, bookings: prev.bookings.map((x) => (x.id === b.id ? b : x)) }));

  const sendDetail = async (r) => {
    const isVideo = r.format === 'video';
    const value = window.prompt(
      isVideo ? `Lien de visioconférence à envoyer à ${r.full_name} :` : `Lieu de la rencontre avec ${r.full_name} (adresse complète) :`,
      isVideo ? r.video_link || videoLink : r.location || ''
    );
    if (!value || !value.trim()) return;
    const res = await authFetch(`/admin/bookings/${r.id}/${isVideo ? 'video-link' : 'location'}`, {
      method: 'POST', json: true, body: JSON.stringify(isVideo ? { link: value.trim() } : { location: value.trim() })
    });
    const out = await res.json();
    if (!res.ok) return window.alert(formatError(out.detail));
    updateBooking(out);
    window.alert(`E-mail envoyé à ${r.email}.`);
  };

  const bookingExtra = (r) => (r.format === 'call' ? null : (
    <button data-testid={`admin-send-detail-${r.id}`} onClick={() => sendDetail(r)} className="body-small"
      style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', textDecoration: 'underline', marginRight: '16px' }}>
      {r.format === 'video' ? (r.video_link ? 'Renvoyer le lien visio' : 'Envoyer le lien visio') : (r.location ? 'Modifier le lieu' : 'Indiquer le lieu')}
    </button>
  ));
  const blockedSet = new Set((data.blocked || []).map((b) => b.date));
  const rows = active === 'bookings' && data.bookings ? data.bookings.map((b) => ({ ...b, blockedDay: blockedSet.has(b.date) })) : data[active];

  const toggleBlocked = async (day) => {
    if (blockedSet.has(day)) {
      const res = await authFetch(`/admin/blocked-days/${day}`, { method: 'DELETE' });
      if (res.ok) setData((prev) => ({ ...prev, blocked: prev.blocked.filter((b) => b.date !== day) }));
      return;
    }
    const res = await authFetch('/admin/blocked-days', { method: 'POST', body: JSON.stringify({ date: day }), json: true });
    if (!res.ok) return;
    const out = await res.json();
    setData((prev) => ({ ...prev, blocked: [...(prev.blocked || []), { date: day }].sort((a, b) => a.date.localeCompare(b.date)) }));
    if (out.existing_bookings.length) {
      const list = out.existing_bookings.map((b) => `• ${b.time} — ${b.full_name} (${b.email})`).join('\n');
      window.alert(`Attention : ${out.existing_bookings.length} rendez-vous déjà pris ce jour-là. Ils sont conservés ; annulez-les depuis l'onglet Rendez-vous si besoin.\n\n${list}`);
    }
  };

  const remove = async (r) => {
    if (active === 'bookings') {
      const note = window.prompt(`Annuler le rendez-vous de ${r.full_name} (${frDay(r.date)} à ${r.time}) ?\n\nUn e-mail d'annulation lui sera envoyé et le créneau redeviendra disponible.\nVous pouvez ajouter un mot personnel (facultatif) :`, '');
      if (note === null) return;
      const res = await authFetch(`/admin/bookings/${r.id}/cancel`, { method: 'POST', body: JSON.stringify({ note }), json: true });
      if (res.ok) setData((prev) => ({ ...prev, bookings: prev.bookings.filter((x) => x.id !== r.id) }));
      return;
    }
    if (!window.confirm(tab.confirm(r))) return;
    const res = await authFetch(`${tab.path}/${r.id}`, { method: 'DELETE' });
    if (res.ok) setData((prev) => ({ ...prev, [active]: prev[active].filter((x) => x.id !== r.id) }));
  };

  if (!token) {
    return <section style={{ minHeight: '100vh', padding: '80px 24px', background: 'var(--bg-primary)' }}><AdminLogin onLogin={login} /></section>;
  }

  return (
    <section style={{ minHeight: '100vh', padding: '80px 24px', background: 'var(--bg-primary)' }}>
      <div className="container" style={{ maxWidth: '1100px' }} data-testid="admin-dashboard">
        <TodayPanel bookings={data.bookings} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '16px', marginBottom: '32px' }}>
          <div>
            <p className="meta-text" style={{ color: 'var(--gold-accent)', marginBottom: '8px' }}>Espace privé Fenicis</p>
            <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
              {Object.entries(TABS).map(([key, t]) => (
                <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setActive(key)} className="heading-3"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', color: active === key ? 'var(--text-primary)' : 'var(--text-light)', borderBottom: active === key ? '1px solid var(--text-primary)' : '1px solid transparent', transition: 'color 0.2s ease' }}>
                  {t.label} <span data-testid={`admin-count-${key}`}>({data[key] ? (key === 'blocked' ? data[key].filter((b) => b.date >= new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' })).length : data[key].length) : '…'})</span>
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px' }}>
            {active !== 'blocked' && <button data-testid="admin-export-csv-button" className="btn-primary" disabled={!rows?.length} onClick={() => downloadCsv(rows, tab.columns, tab.csv)}>Télécharger (CSV)</button>}
            <button data-testid="admin-logout-button" className="btn-secondary" onClick={logout}>Se déconnecter</button>
          </div>
        </div>
        {active === 'bookings' && (
          <div data-testid="admin-video-settings" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '32px', padding: '16px 0', borderBottom: '1px solid var(--border-light)' }}>
            <label htmlFor="video-link" className="body-small" style={{ color: 'var(--text-secondary)' }}>Lien visio habituel</label>
            <input id="video-link" data-testid="admin-video-link-input" type="url" placeholder="https://…" value={videoLink} onChange={(e) => setVideoLink(e.target.value)} style={{ ...fieldStyle, flex: '1 1 320px' }} />
            <button data-testid="admin-video-link-save" className="btn-secondary" onClick={saveVideoLink}>Enregistrer</button>
          </div>
        )}
        {active === 'blocked' && data.blocked && <BlockedDaysPanel blocked={data.blocked} onToggle={toggleBlocked} />}
        {active !== 'blocked' && rows && rows.length === 0 && <p data-testid="admin-empty" className="body-regular">{tab.empty}</p>}
        {active !== 'blocked' && rows && rows.length > 0 && <DataTable tab={tab} rows={rows} onDelete={remove} extra={active === 'bookings' ? bookingExtra : null} />}
      </div>
    </section>
  );
}
